import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { Grid, Tab } from '@leapeasy/ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import React, { useCallback, useEffect, useMemo } from 'react';

import { getBuildingDetailAction } from 'store/actions/propertyActions';

import { openDetails } from 'store/actions/uiActions';
import { useDetailRouteTabs } from 'hooks/useDetailRouteTabs';
import { useHydrateDetailsOnce } from 'hooks/useHydrateDetailsOnce';
import { maskDemoField } from 'utils/demoMask';
import { DetailPageShell, SectionPanelSkeleton, TabBarSkeleton } from 'components/DetailPageState';
import { useDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';
import { filterFlaggedCancellationDetailTabs } from 'utils/flaggedCancellationUi';
import { resolveDetailBackTarget } from 'utils/detailBackNavigation';
import { DetailCard } from './DetailCard';
import {
  ReportTab,
  ApplicationTab,
  PolicyTab,
  ClaimTab,
  InvoiceTab,
  CancellationsTab,
  DetailsTab,
} from './Tabs';

const TAB_SLUGS = [
  'report',
  'applications',
  'policy',
  'claim',
  'invoice',
  'details',
  'flagged-cancellation',
];

const TABS = [
  {
    title: 'Report',
    icon: 'General.Report',
  },
  {
    title: 'Applications',
    icon: 'HomePlaces.MapsHomeWork',
  },
  {
    title: 'Policy',
    icon: 'FileFolder.FindInPage',
  },
  {
    title: 'Claim',
    icon: 'HomePlaces.Gite',
  },
  {
    title: 'Invoice',
    icon: 'FileFolder.Receipt',
  },
  {
    title: 'Details',
    icon: 'FileFolder.Plagiarism',
  },
  {
    title: 'Flagged Cancellation',
    icon: 'General.Flag',
  },
];

const { tabSlugs: BUILDING_TAB_SLUGS, tabs: BUILDING_TABS } = filterFlaggedCancellationDetailTabs(
  TAB_SLUGS,
  TABS,
);

export const BuildingDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { buildingId } = useParams();

  const basePath = useMemo(
    () => (buildingId ? `/property/properties/${buildingId}/detail` : ''),
    [buildingId],
  );
  const { selectedIndex: selectedTab, goToTab } = useDetailRouteTabs({
    basePath,
    tabSlugs: BUILDING_TAB_SLUGS,
  });

  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const detailsJSON = useImmutableJs(details);
  const buildingDetail =
    useSelector((state) => state.getIn(['property', 'building-detail'])) || null;
  const buildingDetailJSON = useImmutableJs(buildingDetail);
  const buildingFromApi = buildingDetailJSON?.data || null;
  const hasUiFallback = !buildingId && detailsJSON?.type === 'property' && detailsJSON?.data;
  const {
    data: building,
    isLoading,
    hasError,
  } = useDetailPageState({
    routeId: buildingId,
    detailRecord: buildingDetailJSON,
    hasUiFallback,
    fallbackData: detailsJSON?.data || null,
  });

  const onClickBack = useCallback(() => {
    dispatch(openDetails(null));
    const target = resolveDetailBackTarget(location.state, '/property/properties');
    if (target.useHistoryBack) {
      navigate(-1);
      return;
    }
    navigate(
      target.path,
      target.state
        ? { state: target.state }
        : {
            buildingsListState: location.state?.buildingsListState || null,
          },
    );
  }, [dispatch, navigate, location.state]);

  const onNavigateToApplications = useCallback(
    (prefilter) => {
      const base = `/property/properties/${buildingId}/detail/applications`;
      const path =
        prefilter != null && String(prefilter).length > 0
          ? `${base}?prefilter=${encodeURIComponent(prefilter)}`
          : base;
      navigate(path, { state: location.state });
    },
    [buildingId, navigate, location.state],
  );

  useEffect(() => {
    if (!buildingId) return;
    dispatch(getBuildingDetailAction(buildingId));
  }, [buildingId, dispatch]);

  const onRetry = useCallback(() => {
    if (buildingId) dispatch(getBuildingDetailAction(buildingId));
  }, [buildingId, dispatch]);

  useHydrateDetailsOnce({
    type: 'property',
    data: buildingFromApi,
    id: buildingFromApi?.building_id,
  });

  useEffect(() => {
    if (buildingId) return;
    if (!details || detailsJSON?.type !== 'property') {
      onClickBack();
    }
  }, [buildingId, details, detailsJSON, onClickBack]);

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      <DetailPageShell
        onBack={onClickBack}
        title={building ? maskDemoField(isDemo, 'building-name', building.name) : null}
        isLoading={isLoading}
        hasError={hasError}
        onRetry={onRetry}
        loadingLabel="Loading property details..."
        skeletonVariant="fields"
      >
        {building ? <DetailCard building={building} isDemo={isDemo} /> : null}
      </DetailPageShell>

      {isLoading ? (
        <Box
          padding="24px 12px"
          borderRadius="12px"
          border="1px solid #EAEAEA"
          mt={1}
          sx={{ background: 'white', overflow: 'visible' }}
        >
          <TabBarSkeleton tabCount={BUILDING_TABS.length} />
          <SectionPanelSkeleton />
        </Box>
      ) : hasError || !building ? null : (
        <Box
          padding="24px 12px"
          borderRadius="12px"
          border="1px solid #EAEAEA"
          mt={1}
          sx={{ background: 'white', overflow: 'visible' }}
        >
          <TabContainer>
            {BUILDING_TABS.map((tabItem, index) => (
              <Tab
                key={BUILDING_TAB_SLUGS[index]}
                title={tabItem.title}
                icon={tabItem.icon}
                isSelected={selectedTab === index}
                onClick={() => goToTab(index)}
              />
            ))}
          </TabContainer>

          <Box marginTop="20px">
            {selectedTab === 0 && (
              <ReportTab building={building} onNavigateToApplications={onNavigateToApplications} />
            )}
            {selectedTab === 1 && <ApplicationTab building={building} />}
            {selectedTab === 2 && <PolicyTab building={building} />}
            {selectedTab === 3 && <ClaimTab building={building} />}
            {selectedTab === 4 && <InvoiceTab building={building} />}
            {selectedTab === 5 && <DetailsTab building={building} />}
            {selectedTab === 6 && <CancellationsTab building={building} />}
          </Box>
        </Box>
      )}
    </DashboardLayoutContainer>
  );
};

const TabContainer = styled(Grid)`
  overflow-x: auto;
  overflow-y: visible;
`;
